import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LabUsageDialog, { countProgrammes } from '../../components/admin/LabUsageDialog';
import axios from 'axios';
import {
  ArrowLeft,
  Save,
  Sparkles,
  Plus,
  Trash2,
  GripVertical,
  MessageSquare,
  ListChecks,
  Info,
  CheckCircle,
  Target,
  Image,
  Volume2,
  ChevronDown,
  ChevronRight,
  Eye,
  Loader2,
  Send,
  Beaker,
  Brain,
  AlertTriangle,
  Award,
  Settings
} from 'lucide-react';
import { toast } from 'sonner';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

const INDUSTRIES = [
  'Financial Services',
  'Healthcare',
  'Technology',
  'Manufacturing',
  'Retail',
  'Government',
  'Education',
  'Legal',
  'Energy',
  'Telecommunications',
  'General Business'
];

const TOPICS = [
  'Anti-Corruption & Bribery',
  'Data Privacy & Protection',
  'Conflict of Interest',
  'Cybersecurity',
  'Workplace Ethics',
  'Anti-Money Laundering',
  'Health & Safety',
  'Harassment Prevention',
  'Information Security',
  'Regulatory Compliance',
  'Risk Management',
  'Corporate Governance'
];

const LEVELS = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' }
];

const STEP_TYPES = [
  { value: 'information', label: 'Information', icon: Info, description: 'Display context without interaction' },
  { value: 'choice', label: 'Decision Point', icon: ListChecks, description: 'Multiple choice decision' },
  { value: 'text_response', label: 'Written Response', icon: MessageSquare, description: 'Free-form text input' },
  { value: 'checkpoint', label: 'NAVI Checkpoint', icon: Brain, description: 'NAVI coaching moment' }
];

const OUTCOME_TYPES = [
  { value: 'optimal', label: 'Optimal', color: 'text-green-400', bg: 'bg-green-500/20' },
  { value: 'acceptable', label: 'Acceptable', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  { value: 'poor', label: 'Poor', color: 'text-orange-400', bg: 'bg-orange-500/20' },
  { value: 'critical_failure', label: 'Critical Failure', color: 'text-red-400', bg: 'bg-red-500/20' }
];

const VOICES = [
  { value: 'nova', label: 'Nova (Female, Warm)' },
  { value: 'alloy', label: 'Alloy (Neutral)' },
  { value: 'echo', label: 'Echo (Male)' },
  { value: 'fable', label: 'Fable (British)' },
  { value: 'onyx', label: 'Onyx (Male, Deep)' },
  { value: 'shimmer', label: 'Shimmer (Female, Clear)' }
];

export default function LearningLabBuilder() {
  const { labId } = useParams();
  const [labUsage, setLabUsage] = useState([]);
  const [showUsage, setShowUsage] = useState(false);
  useEffect(() => {
    if (!labId) return;
    axios.get(`${API}/lab-usage?lab_id=${labId}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => setLabUsage(r.data.programmes || []))
      .catch(() => {});
  }, [labId]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [expandedStep, setExpandedStep] = useState(null);
  
  // Lab data
  const [lab, setLab] = useState({
    title: '',
    description: '',
    industry: '',
    topic: '',
    level: 'intermediate',
    thumbnail_url: '',
    estimated_duration_minutes: 15,
    cpd_points: 0,
    tags: [],
    steps: [],
    outcomes: []
  });
  
  // AI Generation form
  const [aiForm, setAiForm] = useState({
    title: '',
    topic: '',
    industry: '',
    level: 'intermediate',
    outline: '',
    num_decision_points: 5,
    include_images: true,
    include_audio: false,
    voice: 'nova'
  });
  
  const [showAiGenerator, setShowAiGenerator] = useState(false);
  const [generatingMedia, setGeneratingMedia] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (labId) {
      loadLab();
    }
  }, [labId]);

  const loadLab = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/labs/${labId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLab(res.data);
    } catch (error) {
      console.error('Failed to load lab:', error);
      toast.error('Failed to load learning lab');
    } finally {
      setLoading(false);
    }
  };

  const saveLab = async () => {
    setSaving(true);
    try {
      if (labId) {
        await axios.put(`${API}/labs/${labId}`, lab, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Learning lab saved');
      } else {
        const res = await axios.post(`${API}/labs`, lab, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Learning lab created');
        navigate(`/admin/labs/${res.data.lab_id}`);
      }
    } catch (error) {
      console.error('Failed to save lab:', error);
      toast.error('Failed to save learning lab');
    } finally {
      setSaving(false);
    }
  };

  const generateWithAI = async () => {
    if (!aiForm.title || !aiForm.topic || !aiForm.industry || !aiForm.outline) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/labs/generate`, aiForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Learning lab generated successfully!');
      navigate(`/admin/labs/${res.data.lab_id}`);
    } catch (error) {
      console.error('AI generation failed:', error);
      if (error.response?.status === 402) {
        toast.error('Budget exceeded. Please add balance to your Universal Key.');
      } else {
        toast.error('AI generation failed. Please try again.');
      }
    } finally {
      setGenerating(false);
    }
  };

  const publishLab = async () => {
    try {
      await axios.post(`${API}/labs/${labId}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Learning lab published!');
      loadLab();
    } catch (error) {
      console.error('Failed to publish:', error);
      toast.error(error.response?.data?.detail || 'Failed to publish');
    }
  };

  const generateMedia = async (generateImages = true, generateAudio = false) => {
    if (!labId) return;
    
    setGeneratingMedia(true);
    try {
      const res = await axios.post(
        `${API}/labs/${labId}/generate-media?generate_images=${generateImages}&generate_audio=${generateAudio}&voice=${aiForm.voice}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Generated ${res.data.images_generated} images, ${res.data.audio_generated} audio files`);
      loadLab();
    } catch (error) {
      console.error('Media generation failed:', error);
      if (error.response?.status === 402) {
        toast.error('Budget exceeded. Please add balance to your Universal Key.');
      } else {
        toast.error('Media generation failed');
      }
    } finally {
      setGeneratingMedia(false);
    }
  };

  // Step management
  const addStep = () => {
    const newStep = {
      step_id: `step_${Date.now()}`,
      step_type: 'information',
      title: 'New Step',
      context: '',
      image_url: '',
      audio_url: '',
      choices: [],
      evaluation_criteria: '',
      keywords: [],
      ideal_response: '',
      navi_coaching_prompt: '',
      next_step_id: '',
      is_entry_point: lab.steps.length === 0,
      is_terminal: false,
      order: lab.steps.length + 1
    };
    setLab({ ...lab, steps: [...lab.steps, newStep] });
    setExpandedStep(newStep.step_id);
  };

  const updateStep = (stepId, updates) => {
    setLab({
      ...lab,
      steps: lab.steps.map(s => s.step_id === stepId ? { ...s, ...updates } : s)
    });
  };

  const deleteStep = (stepId) => {
    setLab({
      ...lab,
      steps: lab.steps.filter(s => s.step_id !== stepId)
    });
  };

  const addChoice = (stepId) => {
    const step = lab.steps.find(s => s.step_id === stepId);
    const newChoice = {
      choice_id: `choice_${Date.now()}`,
      text: '',
      next_step_id: '',
      feedback: '',
      points: 0,
      outcome_type: null
    };
    updateStep(stepId, { choices: [...(step.choices || []), newChoice] });
  };

  const updateChoice = (stepId, choiceId, updates) => {
    const step = lab.steps.find(s => s.step_id === stepId);
    updateStep(stepId, {
      choices: step.choices.map(c => c.choice_id === choiceId ? { ...c, ...updates } : c)
    });
  };

  const deleteChoice = (stepId, choiceId) => {
    const step = lab.steps.find(s => s.step_id === stepId);
    updateStep(stepId, {
      choices: step.choices.filter(c => c.choice_id !== choiceId)
    });
  };

  // Outcome management
  const addOutcome = () => {
    const newOutcome = {
      outcome_id: `outcome_${Date.now()}`,
      outcome_type: 'acceptable',
      title: 'New Outcome',
      description: '',
      feedback: '',
      learning_points: [],
      image_url: ''
    };
    setLab({ ...lab, outcomes: [...lab.outcomes, newOutcome] });
  };

  const updateOutcome = (outcomeId, updates) => {
    setLab({
      ...lab,
      outcomes: lab.outcomes.map(o => o.outcome_id === outcomeId ? { ...o, ...updates } : o)
    });
  };

  const deleteOutcome = (outcomeId) => {
    setLab({
      ...lab,
      outcomes: lab.outcomes.filter(o => o.outcome_id !== outcomeId)
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-navigate-gold" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {labUsage.length > 0 && (
          <div className="mb-6 flex items-center gap-3 p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-200 text-sm" data-testid="lab-usage-edit-warning">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span className="flex-1">This lab is used in {countProgrammes(labUsage)} programme{countProgrammes(labUsage) === 1 ? '' : 's'} — changes apply everywhere it is linked.</span>
            <button type="button" onClick={() => setShowUsage(true)} className="underline underline-offset-2 hover:text-white" data-testid="lab-usage-view-list-btn">View programmes</button>
          </div>
        )}
        <LabUsageDialog open={showUsage} mode="view" labTitle={lab?.title} usage={labUsage} onClose={() => setShowUsage(false)} />

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/labs')}
              className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-zinc-400" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                <Beaker className="h-6 w-6 text-navigate-gold" />
                {labId ? 'Edit Learning Lab' : 'Create Learning Lab'}
              </h1>
              <p className="text-zinc-400 text-sm mt-1">
                Build interactive workplace scenario simulations
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {!labId && (
              <Button
                variant="outline"
                onClick={() => setShowAiGenerator(!showAiGenerator)}
                className="flex items-center gap-2"
              >
                <Sparkles className="h-4 w-4" />
                AI Generate
              </Button>
            )}
            {labId && lab.status === 'draft' && (
              <Button
                variant="outline"
                onClick={publishLab}
                className="flex items-center gap-2"
              >
                <Eye className="h-4 w-4" />
                Publish
              </Button>
            )}
            {labId && (
              <Button
                variant="outline"
                onClick={() => generateMedia(true, false)}
                disabled={generatingMedia}
                className="flex items-center gap-2"
              >
                {generatingMedia ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Image className="h-4 w-4" />
                )}
                {generatingMedia ? 'Generating...' : 'Generate Images'}
              </Button>
            )}
            <Button
              onClick={saveLab}
              disabled={saving}
              className="flex items-center gap-2 bg-navigate-gold text-black hover:bg-navigate-gold-light"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save
            </Button>
          </div>
        </div>

        {/* AI Generator Panel */}
        {showAiGenerator && (
          <div className="bg-gradient-to-r from-purple-900/30 to-navigate-gold/10 border border-purple-500/30 rounded-xl p-6 mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white">AI Scenario Generator</h3>
                <p className="text-sm text-zinc-400">Let AI create a branching workplace scenario</p>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Scenario Title *</label>
                <Input
                  value={aiForm.title}
                  onChange={(e) => setAiForm({ ...aiForm, title: e.target.value })}
                  placeholder="e.g., Handling a Bribery Attempt"
                  className="bg-zinc-900/50 border-zinc-700"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Topic *</label>
                <Select value={aiForm.topic} onValueChange={(v) => setAiForm({ ...aiForm, topic: v })}>
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-700">
                    <SelectValue placeholder="Select topic" />
                  </SelectTrigger>
                  <SelectContent>
                    {TOPICS.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Industry *</label>
                <Select value={aiForm.industry} onValueChange={(v) => setAiForm({ ...aiForm, industry: v })}>
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-700">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map(i => (
                      <SelectItem key={i} value={i}>{i}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Level</label>
                <Select value={aiForm.level} onValueChange={(v) => setAiForm({ ...aiForm, level: v })}>
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEVELS.map(l => (
                      <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="mb-4">
              <label className="text-sm text-zinc-400 mb-1 block">Scenario Outline *</label>
              <Textarea
                value={aiForm.outline}
                onChange={(e) => setAiForm({ ...aiForm, outline: e.target.value })}
                placeholder="Describe the scenario you want to create. Include the situation, key decision points, and desired learning outcomes..."
                className="bg-zinc-900/50 border-zinc-700 min-h-[120px]"
              />
            </div>
            
            {/* Media Options */}
            <div className="grid md:grid-cols-2 gap-4 mb-4 p-4 bg-zinc-900/30 rounded-lg">
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={aiForm.include_images}
                    onChange={(e) => setAiForm({ ...aiForm, include_images: e.target.checked })}
                    className="rounded border-zinc-600 bg-zinc-800"
                  />
                  <div>
                    <span className="text-sm text-white flex items-center gap-2">
                      <Image className="h-4 w-4 text-blue-400" />
                      Generate Images
                    </span>
                    <span className="text-xs text-zinc-500">AI-generated visuals for each step</span>
                  </div>
                </label>
              </div>
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={aiForm.include_audio}
                    onChange={(e) => setAiForm({ ...aiForm, include_audio: e.target.checked })}
                    className="rounded border-zinc-600 bg-zinc-800"
                  />
                  <div>
                    <span className="text-sm text-white flex items-center gap-2">
                      <Volume2 className="h-4 w-4 text-green-400" />
                      Generate Audio Narration
                    </span>
                    <span className="text-xs text-zinc-500">TTS narration for immersion</span>
                  </div>
                </label>
              </div>
              {aiForm.include_audio && (
                <div className="md:col-span-2">
                  <label className="text-sm text-zinc-400 mb-1 block">Voice</label>
                  <Select value={aiForm.voice} onValueChange={(v) => setAiForm({ ...aiForm, voice: v })}>
                    <SelectTrigger className="bg-zinc-900/50 border-zinc-700 w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VOICES.map(v => (
                        <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <label className="text-sm text-zinc-400">Decision Points:</label>
                <Input
                  type="number"
                  min="3"
                  max="10"
                  value={aiForm.num_decision_points}
                  onChange={(e) => setAiForm({ ...aiForm, num_decision_points: parseInt(e.target.value) || 5 })}
                  className="w-20 bg-zinc-900/50 border-zinc-700"
                />
              </div>
              
              <Button
                onClick={generateWithAI}
                disabled={generating}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate Scenario
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-zinc-800 pb-2">
          {[
            { id: 'details', label: 'Details', icon: Settings },
            { id: 'steps', label: 'Scenario Steps', icon: ListChecks },
            { id: 'outcomes', label: 'Outcomes', icon: Target }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                activeTab === tab.id
                  ? 'bg-navigate-gold text-black'
                  : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.id === 'steps' && lab.steps.length > 0 && (
                <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-black/20">
                  {lab.steps.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'details' && (
          <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Lab Details</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Title *</label>
                <Input
                  value={lab.title}
                  onChange={(e) => setLab({ ...lab, title: e.target.value })}
                  placeholder="Learning Lab Title"
                  className="bg-navigate-dark border-zinc-700"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Topic *</label>
                <Select value={lab.topic} onValueChange={(v) => setLab({ ...lab, topic: v })}>
                  <SelectTrigger className="bg-navigate-dark border-zinc-700">
                    <SelectValue placeholder="Select topic" />
                  </SelectTrigger>
                  <SelectContent>
                    {TOPICS.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Industry *</label>
                <Select value={lab.industry} onValueChange={(v) => setLab({ ...lab, industry: v })}>
                  <SelectTrigger className="bg-navigate-dark border-zinc-700">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRIES.map(i => (
                      <SelectItem key={i} value={i}>{i}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Level</label>
                <Select value={lab.level} onValueChange={(v) => setLab({ ...lab, level: v })}>
                  <SelectTrigger className="bg-navigate-dark border-zinc-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LEVELS.map(l => (
                      <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm text-zinc-400 mb-1 block">Description</label>
                <Textarea
                  value={lab.description}
                  onChange={(e) => setLab({ ...lab, description: e.target.value })}
                  placeholder="Describe the scenario and learning objectives..."
                  className="bg-navigate-dark border-zinc-700 min-h-[100px]"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Estimated Duration (minutes)</label>
                <Input
                  type="number"
                  value={lab.estimated_duration_minutes}
                  onChange={(e) => setLab({ ...lab, estimated_duration_minutes: parseInt(e.target.value) || 15 })}
                  className="bg-navigate-dark border-zinc-700"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">CPD Points</label>
                <Input
                  type="number"
                  step="0.5"
                  value={lab.cpd_points}
                  onChange={(e) => setLab({ ...lab, cpd_points: parseFloat(e.target.value) || 0 })}
                  className="bg-navigate-dark border-zinc-700"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm text-zinc-400 mb-1 block">Thumbnail URL</label>
                <Input
                  value={lab.thumbnail_url || ''}
                  onChange={(e) => setLab({ ...lab, thumbnail_url: e.target.value })}
                  placeholder="https://..."
                  className="bg-navigate-dark border-zinc-700"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'steps' && (
          <div className="space-y-4">
            {lab.steps.length === 0 ? (
              <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-12 text-center">
                <Beaker className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No Scenario Steps Yet</h3>
                <p className="text-zinc-400 mb-4">Add steps to build your interactive scenario</p>
                <Button onClick={addStep} className="bg-navigate-gold text-black hover:bg-navigate-gold-light">
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Step
                </Button>
              </div>
            ) : (
              <>
                {lab.steps.map((step, index) => (
                  <div
                    key={step.step_id}
                    className="bg-navigate-surface border border-zinc-800 rounded-xl overflow-hidden"
                  >
                    {/* Step Header */}
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-zinc-800/50 transition-colors"
                      onClick={() => setExpandedStep(expandedStep === step.step_id ? null : step.step_id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-zinc-500">
                          <GripVertical className="h-5 w-5" />
                        </div>
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          step.step_type === 'choice' ? 'bg-blue-500/20 text-blue-400' :
                          step.step_type === 'text_response' ? 'bg-purple-500/20 text-purple-400' :
                          step.step_type === 'checkpoint' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-zinc-700 text-zinc-400'
                        }`}>
                          {step.step_type === 'choice' ? <ListChecks className="h-4 w-4" /> :
                           step.step_type === 'text_response' ? <MessageSquare className="h-4 w-4" /> :
                           step.step_type === 'checkpoint' ? <Brain className="h-4 w-4" /> :
                           <Info className="h-4 w-4" />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-zinc-500">Step {index + 1}</span>
                            {step.is_entry_point && (
                              <span className="px-2 py-0.5 text-xs bg-green-500/20 text-green-400 rounded">Entry</span>
                            )}
                            {step.is_terminal && (
                              <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded">Terminal</span>
                            )}
                          </div>
                          <h4 className="text-white font-medium">{step.title || 'Untitled Step'}</h4>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteStep(step.step_id); }}
                          className="p-2 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        {expandedStep === step.step_id ? (
                          <ChevronDown className="h-5 w-5 text-zinc-400" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-zinc-400" />
                        )}
                      </div>
                    </div>

                    {/* Step Content (Expanded) */}
                    {expandedStep === step.step_id && (
                      <div className="border-t border-zinc-800 p-4 space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm text-zinc-400 mb-1 block">Step Type</label>
                            <Select
                              value={step.step_type}
                              onValueChange={(v) => updateStep(step.step_id, { step_type: v })}
                            >
                              <SelectTrigger className="bg-navigate-dark border-zinc-700">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {STEP_TYPES.map(t => (
                                  <SelectItem key={t.value} value={t.value}>
                                    <div className="flex items-center gap-2">
                                      <t.icon className="h-4 w-4" />
                                      {t.label}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="text-sm text-zinc-400 mb-1 block">Title</label>
                            <Input
                              value={step.title}
                              onChange={(e) => updateStep(step.step_id, { title: e.target.value })}
                              className="bg-navigate-dark border-zinc-700"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="text-sm text-zinc-400 mb-1 block">Scenario Context</label>
                          <Textarea
                            value={step.context}
                            onChange={(e) => updateStep(step.step_id, { context: e.target.value })}
                            placeholder="Describe the situation the learner faces..."
                            className="bg-navigate-dark border-zinc-700 min-h-[120px]"
                          />
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm text-zinc-400 mb-1 block flex items-center gap-1">
                              <Image className="h-3 w-3" /> Image URL
                            </label>
                            <Input
                              value={step.image_url || ''}
                              onChange={(e) => updateStep(step.step_id, { image_url: e.target.value })}
                              placeholder="https://..."
                              className="bg-navigate-dark border-zinc-700"
                            />
                          </div>
                          <div>
                            <label className="text-sm text-zinc-400 mb-1 block flex items-center gap-1">
                              <Volume2 className="h-3 w-3" /> Audio URL (Narration)
                            </label>
                            <Input
                              value={step.audio_url || ''}
                              onChange={(e) => updateStep(step.step_id, { audio_url: e.target.value })}
                              placeholder="https://..."
                              className="bg-navigate-dark border-zinc-700"
                            />
                          </div>
                        </div>

                        {/* Choice-specific fields */}
                        {step.step_type === 'choice' && (
                          <div className="bg-zinc-900/50 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <label className="text-sm font-medium text-white">Decision Choices</label>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => addChoice(step.step_id)}
                              >
                                <Plus className="h-3 w-3 mr-1" />
                                Add Choice
                              </Button>
                            </div>
                            <div className="space-y-3">
                              {(step.choices || []).map((choice, cidx) => (
                                <div key={choice.choice_id} className="bg-zinc-800/50 rounded-lg p-3">
                                  <div className="flex items-start gap-3">
                                    <span className="text-xs text-zinc-500 mt-2">{String.fromCharCode(65 + cidx)}</span>
                                    <div className="flex-1 space-y-2">
                                      <Input
                                        value={choice.text}
                                        onChange={(e) => updateChoice(step.step_id, choice.choice_id, { text: e.target.value })}
                                        placeholder="Choice text..."
                                        className="bg-navigate-dark border-zinc-700"
                                      />
                                      <div className="grid grid-cols-3 gap-2">
                                        <Select
                                          value={choice.next_step_id || 'none'}
                                          onValueChange={(v) => updateChoice(step.step_id, choice.choice_id, { 
                                            next_step_id: v === 'none' ? '' : v 
                                          })}
                                        >
                                          <SelectTrigger className="bg-navigate-dark border-zinc-700 text-xs">
                                            <SelectValue placeholder="Next step" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="none">No next step</SelectItem>
                                            {lab.steps.filter(s => s.step_id !== step.step_id).map(s => (
                                              <SelectItem key={s.step_id} value={s.step_id}>
                                                {s.title || s.step_id}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        <Select
                                          value={choice.outcome_type || 'none'}
                                          onValueChange={(v) => updateChoice(step.step_id, choice.choice_id, { 
                                            outcome_type: v === 'none' ? null : v 
                                          })}
                                        >
                                          <SelectTrigger className="bg-navigate-dark border-zinc-700 text-xs">
                                            <SelectValue placeholder="Outcome" />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="none">No outcome</SelectItem>
                                            {OUTCOME_TYPES.map(o => (
                                              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                        <Input
                                          type="number"
                                          value={choice.points}
                                          onChange={(e) => updateChoice(step.step_id, choice.choice_id, { 
                                            points: parseInt(e.target.value) || 0 
                                          })}
                                          placeholder="Points"
                                          className="bg-navigate-dark border-zinc-700 text-xs"
                                        />
                                      </div>
                                      <Textarea
                                        value={choice.feedback || ''}
                                        onChange={(e) => updateChoice(step.step_id, choice.choice_id, { feedback: e.target.value })}
                                        placeholder="NAVI feedback for this choice..."
                                        className="bg-navigate-dark border-zinc-700 text-xs min-h-[60px]"
                                      />
                                    </div>
                                    <button
                                      onClick={() => deleteChoice(step.step_id, choice.choice_id)}
                                      className="p-1 text-zinc-500 hover:text-red-400"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Text response fields */}
                        {step.step_type === 'text_response' && (
                          <div className="bg-zinc-900/50 rounded-lg p-4 space-y-3">
                            <div>
                              <label className="text-sm text-zinc-400 mb-1 block">Evaluation Criteria</label>
                              <Textarea
                                value={step.evaluation_criteria || ''}
                                onChange={(e) => updateStep(step.step_id, { evaluation_criteria: e.target.value })}
                                placeholder="What should NAVI look for in the response?"
                                className="bg-navigate-dark border-zinc-700"
                              />
                            </div>
                            <div>
                              <label className="text-sm text-zinc-400 mb-1 block">Keywords (comma-separated)</label>
                              <Input
                                value={(step.keywords || []).join(', ')}
                                onChange={(e) => updateStep(step.step_id, { 
                                  keywords: e.target.value.split(',').map(k => k.trim()).filter(k => k) 
                                })}
                                placeholder="key concepts, important terms"
                                className="bg-navigate-dark border-zinc-700"
                              />
                            </div>
                            <div>
                              <label className="text-sm text-zinc-400 mb-1 block">Ideal Response (for NAVI reference)</label>
                              <Textarea
                                value={step.ideal_response || ''}
                                onChange={(e) => updateStep(step.step_id, { ideal_response: e.target.value })}
                                placeholder="A model answer..."
                                className="bg-navigate-dark border-zinc-700"
                              />
                            </div>
                          </div>
                        )}

                        {/* NAVI coaching prompt */}
                        <div>
                          <label className="text-sm text-zinc-400 mb-1 block flex items-center gap-1">
                            <Brain className="h-3 w-3" /> NAVI Coaching Prompt
                          </label>
                          <Textarea
                            value={step.navi_coaching_prompt || ''}
                            onChange={(e) => updateStep(step.step_id, { navi_coaching_prompt: e.target.value })}
                            placeholder="Guidance for NAVI when coaching at this step..."
                            className="bg-navigate-dark border-zinc-700"
                          />
                        </div>

                        {/* Navigation & flags */}
                        <div className="flex items-center gap-6 pt-4 border-t border-zinc-800">
                          {step.step_type !== 'choice' && (
                            <div className="flex-1">
                              <label className="text-sm text-zinc-400 mb-1 block">Next Step</label>
                              <Select
                                value={step.next_step_id || 'none'}
                                onValueChange={(v) => updateStep(step.step_id, { next_step_id: v === 'none' ? '' : v })}
                              >
                                <SelectTrigger className="bg-navigate-dark border-zinc-700">
                                  <SelectValue placeholder="Select next step" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">None (Terminal)</SelectItem>
                                  {lab.steps.filter(s => s.step_id !== step.step_id).map(s => (
                                    <SelectItem key={s.step_id} value={s.step_id}>
                                      {s.title || s.step_id}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={step.is_entry_point}
                              onChange={(e) => updateStep(step.step_id, { is_entry_point: e.target.checked })}
                              className="rounded border-zinc-600"
                            />
                            <span className="text-sm text-zinc-400">Entry Point</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={step.is_terminal}
                              onChange={(e) => updateStep(step.step_id, { is_terminal: e.target.checked })}
                              className="rounded border-zinc-600"
                            />
                            <span className="text-sm text-zinc-400">Terminal Step</span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                
                <Button
                  onClick={addStep}
                  variant="outline"
                  className="w-full border-dashed"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Step
                </Button>
              </>
            )}
          </div>
        )}

        {activeTab === 'outcomes' && (
          <div className="space-y-4">
            {lab.outcomes.length === 0 ? (
              <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-12 text-center">
                <Target className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No Outcomes Defined</h3>
                <p className="text-zinc-400 mb-4">Define possible outcomes for the scenario</p>
                <Button onClick={addOutcome} className="bg-navigate-gold text-black hover:bg-navigate-gold-light">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Outcome
                </Button>
              </div>
            ) : (
              <>
                {lab.outcomes.map((outcome) => {
                  const outcomeType = OUTCOME_TYPES.find(o => o.value === outcome.outcome_type);
                  return (
                    <div
                      key={outcome.outcome_id}
                      className={`bg-navigate-surface border border-zinc-800 rounded-xl p-6 ${outcomeType?.bg || ''}`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${outcomeType?.bg || 'bg-zinc-700'}`}>
                            {outcome.outcome_type === 'optimal' ? <Award className={`h-5 w-5 ${outcomeType?.color}`} /> :
                             outcome.outcome_type === 'critical_failure' ? <AlertTriangle className={`h-5 w-5 ${outcomeType?.color}`} /> :
                             <Target className={`h-5 w-5 ${outcomeType?.color || 'text-zinc-400'}`} />}
                          </div>
                          <div>
                            <span className={`text-xs ${outcomeType?.color || 'text-zinc-400'}`}>
                              {outcomeType?.label || 'Outcome'}
                            </span>
                            <h4 className="text-white font-medium">{outcome.title}</h4>
                          </div>
                        </div>
                        <button
                          onClick={() => deleteOutcome(outcome.outcome_id)}
                          className="p-2 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="text-sm text-zinc-400 mb-1 block">Outcome Type</label>
                          <Select
                            value={outcome.outcome_type}
                            onValueChange={(v) => updateOutcome(outcome.outcome_id, { outcome_type: v })}
                          >
                            <SelectTrigger className="bg-navigate-dark border-zinc-700">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {OUTCOME_TYPES.map(o => (
                                <SelectItem key={o.value} value={o.value}>
                                  <span className={o.color}>{o.label}</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-sm text-zinc-400 mb-1 block">Title</label>
                          <Input
                            value={outcome.title}
                            onChange={(e) => updateOutcome(outcome.outcome_id, { title: e.target.value })}
                            className="bg-navigate-dark border-zinc-700"
                          />
                        </div>
                      </div>

                      <div className="mb-4">
                        <label className="text-sm text-zinc-400 mb-1 block">Description</label>
                        <Textarea
                          value={outcome.description}
                          onChange={(e) => updateOutcome(outcome.outcome_id, { description: e.target.value })}
                          placeholder="What happened as a result of the learner's decisions..."
                          className="bg-navigate-dark border-zinc-700"
                        />
                      </div>

                      <div className="mb-4">
                        <label className="text-sm text-zinc-400 mb-1 block">Feedback</label>
                        <Textarea
                          value={outcome.feedback}
                          onChange={(e) => updateOutcome(outcome.outcome_id, { feedback: e.target.value })}
                          placeholder="Comprehensive feedback for the learner..."
                          className="bg-navigate-dark border-zinc-700"
                        />
                      </div>

                      <div>
                        <label className="text-sm text-zinc-400 mb-1 block">Learning Points (one per line)</label>
                        <Textarea
                          value={(outcome.learning_points || []).join('\n')}
                          onChange={(e) => updateOutcome(outcome.outcome_id, { 
                            learning_points: e.target.value.split('\n').filter(l => l.trim()) 
                          })}
                          placeholder="Key takeaways..."
                          className="bg-navigate-dark border-zinc-700"
                        />
                      </div>
                    </div>
                  );
                })}
                
                <Button
                  onClick={addOutcome}
                  variant="outline"
                  className="w-full border-dashed"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Outcome
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
